import React, { useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import {
  HelpCircle,
  X,
  PlayCircle,
  CheckCircle2,
  MinusCircle,
  Sparkles,
  Circle,
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext.jsx';
import { useLanguage } from '../contexts/LanguageContext.jsx';
import {
  ALL_TOUR_IDS,
  FAB_PULSE_KEY_PREFIX,
  TOUR_I18N_KEYS,
} from './onboardingConfig.js';
import { getTourStatus } from './onboardingStorage.js';
import { useOnboardingContext } from './onboardingContext.js';
import {
  FLOATING_DOCK_EVENT,
  DOCK_PANEL_WIDTH,
  DOCK_PANEL_MAX_H,
  DOCK_PANEL_RIGHT,
  DOCK_PANEL_BOTTOM,
  DOCK_PANEL_SHELL,
  DOCK_PANEL_HEADER,
  DOCK_PANEL_FOOTER,
  DOCK_RIGHT,
  DOCK_BOTTOM,
  DOCK_FAB_BUTTON,
  FLOATING_DOCK_CLASS,
  announceDockOpen,
} from '../utils/floatingDocks.js';

function StatusIcon({ status }) {
  if (status === 'completed') {
    return <CheckCircle2 className="w-4 h-4 text-green-600 shrink-0" aria-hidden />;
  }
  if (status === 'skipped') {
    return <MinusCircle className="w-4 h-4 text-amber-500 shrink-0" aria-hidden />;
  }
  return <span className="w-4 h-4 rounded-full border-2 border-gray-300 shrink-0" aria-hidden />;
}

export default function FloatingOnboardingAssistant({ dockAboveFeedback = true }) {
  const { currentUser } = useAuth();
  const { t } = useLanguage();
  const {
    tourProgress,
    resumeTour,
    skipTour,
    isRunning,
    activeTour,
    personaModalVisible,
    impersonationActive,
    disabled,
    canStartTour,
    getAssistantPayload,
    tourContext,
  } = useOnboardingContext();

  const [open, setOpen] = useState(false);
  const [pulseOnce, setPulseOnce] = useState(false);
  const [startError, setStartError] = useState(null);
  const [assistantData, setAssistantData] = useState(null);
  const [assistantLoading, setAssistantLoading] = useState(false);

  const uid = currentUser?.uid;
  const persona = tourContext?.persona;

  useEffect(() => {
    if (!uid || personaModalVisible) return;
    const key = `${FAB_PULSE_KEY_PREFIX}${uid}`;
    if (localStorage.getItem(key)) return;
    setPulseOnce(true);
    localStorage.setItem(key, '1');
    const timer = window.setTimeout(() => setPulseOnce(false), 1600);
    return () => window.clearTimeout(timer);
  }, [uid, personaModalVisible]);

  useEffect(() => {
    if (!open || !getAssistantPayload) return undefined;
    let cancelled = false;
    setAssistantLoading(true);
    getAssistantPayload()
      .then((data) => {
        if (!cancelled) setAssistantData(data);
      })
      .finally(() => {
        if (!cancelled) setAssistantLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [open, getAssistantPayload, tourProgress, tourContext?.currentStep, persona]);

  useEffect(() => {
    if (!open) return undefined;
    announceDockOpen('help');
    const onOtherDock = (e) => {
      if (e.detail?.id && e.detail.id !== 'help') setOpen(false);
    };
    window.addEventListener(FLOATING_DOCK_EVENT, onOtherDock);
    return () => window.removeEventListener(FLOATING_DOCK_EVENT, onOtherDock);
  }, [open]);

  const hidden = !uid || personaModalVisible || impersonationActive || disabled;

  const tours = useMemo(
    () =>
      ALL_TOUR_IDS.map((id) => ({
        id,
        labelKey: TOUR_I18N_KEYS[id],
        status: getTourStatus(tourProgress, id),
        startable: canStartTour(id),
      })),
    [tourProgress, canStartTour],
  );

  const handleStartTour = async (tourId) => {
    setStartError(null);
    const ok = await resumeTour(tourId);
    if (!ok) {
      setStartError(t('tour.assistant.startError'));
      return;
    }
    setOpen(false);
  };

  if (hidden) return null;

  const greeting = assistantData?.greetingKey ? t(assistantData.greetingKey) : t('tour.assistant.title');
  const suggestedTour = assistantData?.suggestedTour;

  const panel = open ? (
    <div
      className={`fixed ${DOCK_PANEL_BOTTOM} ${DOCK_PANEL_RIGHT} z-[199] ${FLOATING_DOCK_CLASS} rentalyzer-onboarding-panel-enter pointer-events-auto ${DOCK_PANEL_WIDTH} ${DOCK_PANEL_MAX_H} ${DOCK_PANEL_SHELL} overflow-hidden flex flex-col`}
      role="dialog"
      aria-label={t('tour.assistant.title')}
    >
      <div className={`flex items-start justify-between gap-2 px-4 py-3 ${DOCK_PANEL_HEADER} shrink-0`}>
        <div className="min-w-0">
          <p className="text-sm font-semibold text-gray-900 flex items-center gap-1.5">
            <Sparkles className="w-4 h-4 text-blue-600 shrink-0" aria-hidden />
            {t('tour.assistant.title')}
          </p>
          <p className="text-xs text-gray-600 mt-1 leading-snug">{greeting}</p>
        </div>
        <button
          type="button"
          onClick={() => setOpen(false)}
          className="p-1 rounded-md text-gray-400 hover:text-gray-600 hover:bg-gray-100 shrink-0"
          aria-label={t('close')}
        >
          <X className="w-4 h-4" />
        </button>
      </div>

      <div className="overflow-y-auto flex-1 min-h-0">
        {suggestedTour && !isRunning ? (
          <div className="px-4 pt-3 pb-2">
            <p className="text-[10px] font-semibold uppercase tracking-wide text-blue-700 mb-1.5">
              {t('tour.assistant.recommended')}
            </p>
            <button
              type="button"
              onClick={() => handleStartTour(suggestedTour)}
              className="rentalyzer-suggestion-primary w-full flex items-center gap-2 px-3 py-2.5 rounded-lg bg-blue-600 text-white text-sm font-medium hover:bg-blue-700 transition-colors text-left"
            >
              <PlayCircle className="w-4 h-4 shrink-0" />
              <span className="flex-1">{t(`tour.assistant.suggest.${suggestedTour}`)}</span>
            </button>
          </div>
        ) : null}

        {assistantData?.checklist?.length ? (
          <div className="px-4 py-2 border-b border-gray-100">
            <p className="text-[10px] font-semibold uppercase tracking-wide text-gray-500 mb-2">
              {t('tour.checklist.title')}
            </p>
            <ul className="space-y-1.5">
              {assistantData.checklist.map((item) => (
                <li key={item.id} className="flex items-center gap-2 text-xs text-gray-700">
                  {item.done ? (
                    <CheckCircle2 className="w-3.5 h-3.5 text-green-600 shrink-0" aria-hidden />
                  ) : (
                    <Circle className="w-3.5 h-3.5 text-gray-300 shrink-0" aria-hidden />
                  )}
                  <span className={item.done ? 'text-gray-500 line-through' : ''}>
                    {t(item.labelKey)}
                  </span>
                </li>
              ))}
            </ul>
          </div>
        ) : null}

        {assistantLoading ? (
          <p className="px-4 py-3 text-xs text-gray-400">{t('loading')}</p>
        ) : null}

        {!assistantLoading && assistantData?.suggestions?.length ? (
          <div className="px-4 py-2 border-b border-gray-100">
            <p className="text-[10px] font-semibold uppercase tracking-wide text-gray-500 mb-2">
              {t('tour.assistant.suggestionsTitle')}
            </p>
            <ul className="space-y-1.5">
              {assistantData.suggestions.map((tip) => (
                <li key={tip.id}>
                  {tip.action === 'startTour' && tip.tourId ? (
                    <button
                      type="button"
                      disabled={isRunning}
                      onClick={() => handleStartTour(tip.tourId)}
                      className="rentalyzer-suggestion-tip w-full text-left text-xs text-gray-700 px-2 py-1.5 rounded-md hover:bg-blue-50 hover:text-blue-900 disabled:opacity-50"
                    >
                      {t(tip.messageKey)}
                    </button>
                  ) : (
                    <p className="text-xs text-gray-600 px-2 py-1 leading-snug">{t(tip.messageKey)}</p>
                  )}
                </li>
              ))}
            </ul>
          </div>
        ) : null}

        <div className="px-4 pt-2 pb-1">
          <p className="text-[10px] font-semibold uppercase tracking-wide text-gray-500 mb-1">
            {t('tour.assistant.allTours')}
          </p>
        </div>
        <ul className="pb-2">
          {tours.map((tour) => (
            <li key={tour.id}>
              <button
                type="button"
                disabled={isRunning}
                onClick={() => handleStartTour(tour.id)}
                className="flex w-full items-center gap-2 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 disabled:opacity-50 text-left"
                title={!tour.startable ? t('tour.assistant.needsContext') : undefined}
              >
                <StatusIcon status={tour.status} />
                <span className="flex-1 truncate">{t(tour.labelKey)}</span>
                <PlayCircle className="w-4 h-4 text-blue-600 shrink-0 opacity-80" aria-hidden />
              </button>
            </li>
          ))}
        </ul>
      </div>

      {startError ? (
        <p className="px-4 py-2 text-xs text-amber-700 border-t border-amber-100 bg-amber-50 shrink-0">
          {startError}
        </p>
      ) : null}

      <div className={`flex flex-col gap-2 p-3 ${DOCK_PANEL_FOOTER} shrink-0`}>
        <p className="text-[10px] text-gray-400 text-center leading-snug">
          {t('tour.assistant.aiComingSoon')}
        </p>
        {isRunning && activeTour ? (
          <button
            type="button"
            onClick={() => {
              skipTour(activeTour);
              setOpen(false);
            }}
            className="w-full py-2 px-3 rounded-lg border border-gray-200 bg-white text-sm text-gray-600 hover:bg-gray-50 transition-colors"
          >
            {t('tour.actions.skip')}
          </button>
        ) : null}
      </div>
    </div>
  ) : null;

  return createPortal(
    <>
      {panel}
      <div
        className={`fixed ${DOCK_BOTTOM} ${dockAboveFeedback ? DOCK_RIGHT.help : DOCK_RIGHT.feedback} z-[199] ${FLOATING_DOCK_CLASS} pointer-events-none`}
      >
        <button
          type="button"
          data-onboarding="tour-assistant-fab"
          onClick={() => {
            setStartError(null);
            setOpen((v) => !v);
          }}
          className={`rentalyzer-onboarding-fab-enter pointer-events-auto flex items-center justify-center ${DOCK_FAB_BUTTON} rounded-full bg-blue-600 text-white shadow-lg hover:bg-blue-700 transition-colors focus:outline-none focus:ring-2 focus:ring-blue-400 focus:ring-offset-2 ${
            pulseOnce ? 'rentalyzer-onboarding-fab-pulse-once' : ''
          }`}
          aria-expanded={open}
          aria-label={t('tour.assistant.title')}
          title={t('tour.assistant.pulseHint')}
        >
          <HelpCircle className="w-5 h-5 sm:w-6 sm:h-6" />
        </button>
      </div>
    </>,
    document.body,
  );
}
